import React, { useState, useEffect } from 'react'
import { MapContainer, TileLayer, CircleMarker, useMap } from 'react-leaflet'
import useGnssStore from '../../../stores/gnssStore';

import 'leaflet/dist/leaflet.css';

// Define Recenter outside component to prevent recreation
const Recenter = ({ lat, lng }) => {
  const map = useMap();
  const prevLatLngRef = React.useRef({ lat: null, lng: null });

  useEffect(() => {
    // Only recenter if lat/lng actually changed
    if (prevLatLngRef.current.lat !== lat || prevLatLngRef.current.lng !== lng) {
      console.log('Recenter: map.setView called with', lat, lng, 'previous was', prevLatLngRef.current);
      map.setView([lat, lng], 18);
      prevLatLngRef.current = { lat, lng };
    }
  }, [lat, lng, map]);
  return null;
};

function CoordinateMapView(props) {
  // Track the center that should trigger recentering
  const [centerToRecenter, setCenterToRecenter] = useState({
    lat: props.center[0] || 0.0,
    lng: props.center[1] || 0.0
  });
  const [highlight, setHighlight] = useState([0.0, 0.0]);

  // Subscribe to individual GNSS values to prevent infinite loops
  const gnssLatitude = useGnssStore((state) => state.latitude);
  const gnssLongitude = useGnssStore((state) => state.longitude);
  const gnssFixQuality = useGnssStore((state) => state.fixQuality);

  // Create livePosition object from individual values
  const livePosition = gnssLatitude && gnssLongitude && gnssFixQuality > 0
    ? { latitude: gnssLatitude, longitude: gnssLongitude }
    : null;

  // Only update center when it explicitly changes (user clicks "Zoom to Current")
  useEffect(() => {
    const newLat = props.center[0];
    const newLng = props.center[1];

    // Only recenter if the center actually changed (user action, not data update)
    if (newLat && newLng && (String(centerToRecenter.lat) !== String(newLat) || String(centerToRecenter.lng) !== String(newLng))) {
      console.log('CoordinateMapView: Center changed, will recenter map from', centerToRecenter, 'to', { lat: newLat, lng: newLng });
      setCenterToRecenter({ lat: newLat, lng: newLng });
    }
  }, [props.center]);

  useEffect(() => {
    setHighlight(props.highlight);
  }, [props.highlight]);

  return (
    <MapContainer
      center={[props.center[0] || 0, props.center[1] || 0]}
      zoom={props.zoom || 10}
      maxZoom={30}
      style={{
        height: "50vh", borderRadius: "0px",
        width: "100%",
        minWidth: "100px",
      }}
    >
      <Recenter lat={centerToRecenter.lat} lng={centerToRecenter.lng} />
      <TileLayer
        // attribution='&copy <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {
        props.coordinates.map((coord, index) => {
          return <CircleMarker
            key={"ref-coord" + index}
            center={[coord.latitude, coord.longitude]}
            pathOptions={{ fillColor: 'black', color: 'black' }}
            radius={3}>
          </CircleMarker>
        })
      }
      <CircleMarker
        key="current-ref-coord"
        center={[props.coordinates[0].latitude, props.coordinates[0].longitude]}
        pathOptions={{ fillColor: 'green', color: 'green' }}
        radius={3}>
      </CircleMarker>

      {highlight != undefined &&
        <CircleMarker
          key="highlight-ref-coord"
          center={highlight}
          pathOptions={{ fillColor: '', color: 'yellow' }}
          radius={4}>
        </CircleMarker>
      }

      {livePosition != null &&
        <CircleMarker
          key="live-gnss-position"
          center={[livePosition.latitude, livePosition.longitude]}
          pathOptions={{ fillColor: 'blue', color: 'blue' }}
          radius={5}>
        </CircleMarker>
      }

    </ MapContainer >);
}

// Memoize the component and provide custom comparison for props
// Note: livePosition is now from Zustand, not props, so we don't check it here
export default React.memo(CoordinateMapView, (prevProps, nextProps) => {
  // Only re-render if these values actually change
  const centerUnchanged =
    prevProps.center[0] === nextProps.center[0] &&
    prevProps.center[1] === nextProps.center[1];

  const highlightUnchanged =
    prevProps.highlight?.[0] === nextProps.highlight?.[0] &&
    prevProps.highlight?.[1] === nextProps.highlight?.[1];

  // For coordinates, check if all coordinate positions have changed, not just the first
  // This prevents re-render when the same coordinates are in a different array reference
  const coordinatesUnchanged =
    prevProps.coordinates.length === nextProps.coordinates.length &&
    prevProps.coordinates.every((coord, index) => {
      const nextCoord = nextProps.coordinates[index];
      return coord?.latitude === nextCoord?.latitude &&
             coord?.longitude === nextCoord?.longitude;
    });

  const zoomUnchanged = prevProps.zoom === nextProps.zoom;

  const shouldNotRerender = centerUnchanged && highlightUnchanged &&
                            coordinatesUnchanged && zoomUnchanged;

  if (!shouldNotRerender) {
    console.log('CoordinateMapView React.memo: Will re-render because:', {
      centerUnchanged,
      highlightUnchanged,
      coordinatesUnchanged,
      zoomUnchanged,
      prevCenter: prevProps.center,
      nextCenter: nextProps.center,
      prevCoordinatesLength: prevProps.coordinates.length,
      nextCoordinatesLength: nextProps.coordinates.length,
      prevFirstCoord: prevProps.coordinates[0],
      nextFirstCoord: nextProps.coordinates[0]
    });
  }

  return shouldNotRerender;
});